import React, { useState, useEffect } from 'react'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useSelector } from 'react-redux'
import { TemplateListSlice } from '../../reducers'
import { useTenantFormContext } from '../../pages/tenant-edit/tenant-form-context'
import { Accordion, AccordionSummary, AccordionDetails, Typography, Button, Box, Grid, CircularProgress } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { AccountTreeOutlined } from '@mui/icons-material'

const INITIAL_VISIBLE_COUNT = 5

const TemplateSelect = ({ onTemplateSelect = () => { } }) => {
  const [expanded, setExpanded] = useState(false)
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT)
  const templates = useSelector(TemplateListSlice.selectors.selectParentTemplatesList)
  const { isEdit } = useTenantFormContext()

  const [getParentTemplatesList] = useEvent([TemplateListSlice.eventBus.getParentTemplatesList])

  useEffect(() => {
    if (isEdit && !templates.length) {
      getParentTemplatesList.trigger({ populate: true })
    }
  }, [isEdit, getParentTemplatesList, templates.length])

  const handleLoadMore = () => {
    if (visibleCount < templates.length) {
      setVisibleCount(templates.length)
    } else {
      setVisibleCount(INITIAL_VISIBLE_COUNT)
    }
  }

  if (!templates.length || getParentTemplatesList.isWorking) {
    return (
      <Grid container justifyContent='center' alignItems='center' gap={1}>
        <CircularProgress size={15} />
        <Typography variant='body2' color='textSecondary'>
          Loading Templates...
        </Typography>
      </Grid>
    )
  }

  return (
    <Box data-test='content-hub-template-selector'>
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary sx={{ backgroundColor: '#F8F8F8' }}>
          <Box width='100%'>
            <Grid container alignItems='center'>
              <Grid item>
                {expanded
                  ? <RemoveIcon style={{ marginRight: 8, marginTop: 8 }} />
                  : <AddIcon style={{ marginRight: 8, marginTop: 8 }} />}
              </Grid>
              <Grid item>
                <Typography variant='body1' sx={{ fontSize: 15, fontWeight: 400, color: '#000000DE' }}>
                  Templates
                </Typography>
              </Grid>
            </Grid>
            <Typography variant='body2' color='textSecondary' style={{ marginTop: 2 }}>
              Select the template you want to use as a basis. You can also create
              new templates from any template base in the Template option on the
              top navbar.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1} data-test='content-hub-template-list'>
            {templates.slice(0, visibleCount).map((template, index) => (
              <Grid
                item xs={12} key={index}
                data-test={`content-hub-template-list-item-${index}`}
              >
                <Box
                  padding={2}
                  border='1px solid #E0E0E0'
                  borderRadius={2}
                >
                  <Grid container alignItems='center'>
                    <Grid
                      item xs={6}
                      data-test={`content-hub-template-list-item-${index}-name`}
                    >
                      <Typography>{template.name}</Typography>
                    </Grid>
                    {template.parent && (
                      <Grid item xs={4} data-test={`content-hub-template-list-item-${index}-parent`}>
                        <Box display='flex' alignItems='center' marginTop={1} spacing={1}>
                          <AccountTreeOutlined style={{ marginRight: 4, color: '#666666' }} />
                          <Typography variant='caption' color='textSecondary'>
                            {template.parent.name}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={template.parent ? 2 : 6} display='flex' justifyContent='flex-end'>
                      <Button
                        variant='contained' color='primary' onClick={() => onTemplateSelect(template)}
                        data-test={`content-hub-template-list-item-${index}-use-button`}
                      >
                        Use It
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))}
            {templates.length > INITIAL_VISIBLE_COUNT && (
              <Grid item xs={12}>
                <Box display='flex' justifyContent='center'>
                  <Button
                    variant='contained' color='primary' onClick={handleLoadMore}
                    data-test={visibleCount < templates.length
                      ? 'content-hub-template-list-load-more-button'
                      : 'content-hub-template-list-show-less-button'}
                  >
                    {visibleCount < templates.length ? 'Load More' : 'Show Less'}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default TemplateSelect
